import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const BADGES: Record<string, { name: string; emoji: string; desc: string }> = {
  first_whistle: { name: 'First Whistle', emoji: '📣', desc: 'Created your first team' },
  full_roster: { name: 'Full Roster', emoji: '👥', desc: 'Added 10+ players' },
  playmaker: { name: 'Playmaker', emoji: '📋', desc: 'Assigned 10+ lessons' },
  season_coach: { name: 'Season Coach', emoji: '📅', desc: 'Active for 30 days' },
  championship_staff: { name: 'Championship Staff', emoji: '🏆', desc: 'Every player on a team completed a lesson' },
};

type Badge = { id: string; earned: boolean; progress: number; goal: number };

/** Coach achievement badges, computed server-side from real team data. */
export function CoachBadges({ refreshKey }: { refreshKey?: unknown }) {
  const { user } = useAuth();
  const [badges, setBadges] = useState<Badge[]>([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase.rpc('get_coach_badges' as never).then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      const list = data as Badge[];
      setBadges(list);
      const key = `sportara-coach-badges-seen:${user.id}`;
      const seen = new Set<string>(JSON.parse(localStorage.getItem(key) || '[]'));
      const fresh = list.filter(b => b.earned && !seen.has(b.id));
      fresh.forEach(b => toast.success(`Badge unlocked: ${BADGES[b.id]?.name ?? b.id} ${BADGES[b.id]?.emoji ?? ''}`, { description: 'Congratulations, Coach!' }));
      if (fresh.length) localStorage.setItem(key, JSON.stringify([...seen, ...fresh.map(b => b.id)]));
    });
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  if (!badges.length) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1 mt-1" aria-label="Coach badges">
        {badges.map(b => {
          const meta = BADGES[b.id];
          if (!meta) return null;
          return (
            <Tooltip key={b.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`${meta.name}: ${b.earned ? 'earned' : `${b.progress}/${b.goal}`}`}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ring-1 transition ${b.earned ? 'bg-primary/15 ring-primary/40' : 'bg-muted/40 ring-border grayscale opacity-40'}`}
                >
                  {meta.emoji}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-display font-bold text-xs">{meta.name}</p>
                <p className="text-[11px] text-muted-foreground">{meta.desc}{b.earned ? ' — earned' : ` (${Math.min(b.progress, b.goal)}/${b.goal})`}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
